import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Bug, HelpCircle, BookOpen, Baby, CreditCard, Sparkles, MessageSquare, Trash2 } from "lucide-react";
import { useLang } from "@/hooks/use-language";
import { usePermissions } from "@/hooks/use-permissions";
import { listFeedback, updateFeedback, addFeedbackNote, deleteFeedback } from "@/lib/feedback.functions";
import { sendParentMessage } from "@/lib/parent-messages.functions";
import { BackButton } from "@/components/BackButton";

export const Route = createFileRoute("/_authenticated/app/admin/feedback")({
  component: FeedbackAdmin,
});

type Status = "new" | "reviewed" | "resolved";
type Priority = "low" | "medium" | "high";
type Cat = "bug" | "confusing_screen" | "content" | "child_reaction" | "billing" | "suggestion";

type Row = {
  id: string;
  user_id: string;
  user_email: string | null;
  child_id: string | null;
  category: Cat;
  message: string;
  route: string | null;
  user_agent: string | null;
  locale: string | null;
  app_version: string | null;
  status: Status;
  priority: Priority;
  assignee_id: string | null;
  internal_notes: Array<{ at: string; by: string; by_email: string | null; note: string }>;
  created_at: string;
  updated_at: string;
};

const CAT_META: Record<Cat, { icon: typeof Bug; en: string; ar: string }> = {
  bug:              { icon: Bug,        en: "Bug",          ar: "خلل" },
  confusing_screen: { icon: HelpCircle, en: "Confusing",    ar: "محيّرة" },
  content:          { icon: BookOpen,   en: "Content",      ar: "محتوى" },
  child_reaction:   { icon: Baby,       en: "Child",        ar: "طفل" },
  billing:          { icon: CreditCard, en: "Billing",      ar: "دفع" },
  suggestion:       { icon: Sparkles,   en: "Suggestion",   ar: "اقتراح" },
};

const STATUS_COLOR: Record<Status, string> = {
  new: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  reviewed: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  resolved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
};

const PRIORITY_COLOR: Record<Priority, string> = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-primary/10 text-primary",
  high: "bg-destructive/15 text-destructive",
};

function FeedbackAdmin() {
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const perms = usePermissions();
  const list = useServerFn(listFeedback);
  const upd = useServerFn(updateFeedback);
  const addNote = useServerFn(addFeedbackNote);
  const sendMsg = useServerFn(sendParentMessage);
  const delFn = useServerFn(deleteFeedback);
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<Status | "all">("new");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});
  const [replyDraft, setReplyDraft] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["admin-feedback", statusFilter],
    queryFn: () =>
      list({
        data: { status: statusFilter === "all" ? null : statusFilter, limit: 100 },
      }),
  });

  const updMut = useMutation({
    mutationFn: (v: { id: string; status?: Status; priority?: Priority }) =>
      upd({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-feedback"] }),
    onError: () => toast.error(isAr ? "فشل التحديث" : "Update failed"),
  });

  const noteMut = useMutation({
    mutationFn: (v: { id: string; note: string }) => addNote({ data: v }),
    onSuccess: (_d, v) => {
      setNoteDraft((d) => ({ ...d, [v.id]: "" }));
      qc.invalidateQueries({ queryKey: ["admin-feedback"] });
      toast.success(isAr ? "تمت الإضافة" : "Note added");
    },
  });

  const replyMut = useMutation({
    mutationFn: (v: { feedbackId: string; parent_user_id: string; body: string }) =>
      sendMsg({
        data: {
          parent_user_id: v.parent_user_id,
          body: v.body,
          related_feedback_id: v.feedbackId,
          notify_email: true,
        },
      }),
    onSuccess: (_d, v) => {
      setReplyDraft((d) => ({ ...d, [v.feedbackId]: "" }));
      toast.success(isAr ? "أُرسلت الرسالة 💌" : "Reply sent 💌");
    },
    onError: (e: unknown) =>
      toast.error(
        isAr
          ? `لم نقدر نرسل: ${e instanceof Error ? e.message : "خطأ"}`
          : `Couldn't send: ${e instanceof Error ? e.message : "error"}`,
      ),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feedback"] });
      toast.success(isAr ? "تم الحذف" : "Deleted");
    },
    onError: (e: unknown) =>
      toast.error(
        isAr
          ? `فشل الحذف: ${e instanceof Error ? e.message : "خطأ"}`
          : `Delete failed: ${e instanceof Error ? e.message : "error"}`,
      ),
  });

  if (perms.loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }
  if (!perms.canModerate && !perms.canViewUsers) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        {isAr ? "ليس لديك صلاحية الوصول." : "You don't have access."}
      </div>
    );
  }

  const rows = (data?.rows ?? []) as unknown as Row[];

  return (
    <div className="mx-auto max-w-4xl px-4 py-6" dir={dir}>
      <div className="mb-3"><BackButton /></div>
      <header className="mb-5">
        <h1 className="text-2xl font-bold">
          {isAr ? "ملاحظات تجريبية" : "Beta feedback"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isAr
            ? "ما يصلنا من الأهل خلال الفترة التجريبية."
            : "What parents are sending us during beta."}
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2">
        {(["new", "reviewed", "resolved", "all"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              statusFilter === s
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/70"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          {isAr ? "لا شيء بعد." : "Nothing yet."}
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => {
            const meta = CAT_META[r.category];
            const Icon = meta.icon;
            const isOpen = expanded === r.id;
            return (
              <li
                key={r.id}
                className="rounded-3xl border border-border bg-card p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {isAr ? meta.ar : meta.en}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_COLOR[r.status]}`}>
                        {r.status}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PRIORITY_COLOR[r.priority]}`}>
                        {r.priority}
                      </span>
                      <span className="ms-auto text-[11px] text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{r.message}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      {r.route && <span>📍 {r.route}</span>}
                      {r.locale && <span>🌐 {r.locale}</span>}
                      {r.app_version && <span>⚙ {r.app_version}</span>}
                      {r.user_email ? (
                        <a
                          href={`mailto:${r.user_email}?subject=${encodeURIComponent(
                            isAr ? "ردّاً على ملاحظتك في جذوري" : "Re: your note on Jozori",
                          )}&body=${encodeURIComponent(
                            (isAr ? "مرحباً،\n\nشكراً لملاحظتك:\n> " : "Hi,\n\nThank you for your note:\n> ") +
                              r.message.split("\n").join("\n> ") +
                              (isAr ? "\n\n— فريق جذوري" : "\n\n— The Jozori team"),
                          )}`}
                          className="underline-offset-2 hover:underline"
                        >
                          ✉️ {r.user_email}
                        </a>
                      ) : (
                        <span>👤 {r.user_id.slice(0, 8)}</span>
                      )}
                      {r.child_id && <span>🧒 {r.child_id.slice(0, 8)}</span>}
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <select
                        value={r.status}
                        onChange={(e) =>
                          updMut.mutate({ id: r.id, status: e.target.value as Status })
                        }
                        className="rounded-full border border-border bg-background px-2 py-1 text-xs"
                      >
                        <option value="new">new</option>
                        <option value="reviewed">reviewed</option>
                        <option value="resolved">resolved</option>
                      </select>
                      <select
                        value={r.priority}
                        onChange={(e) =>
                          updMut.mutate({ id: r.id, priority: e.target.value as Priority })
                        }
                        className="rounded-full border border-border bg-background px-2 py-1 text-xs"
                      >
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                      </select>
                      <button
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-xs hover:bg-muted/60"
                      >
                        <MessageSquare className="h-3 w-3" />
                        {r.internal_notes?.length ?? 0} {isAr ? "ملاحظة" : "notes"}
                      </button>
                      {perms.isSuperAdmin && (
                        <button
                          disabled={delMut.isPending}
                          onClick={() => {
                            if (
                              confirm(
                                isAr
                                  ? "حذف هذه الرسالة نهائياً؟ لا يمكن التراجع."
                                  : "Delete this message permanently? This cannot be undone.",
                              )
                            ) {
                              delMut.mutate(r.id);
                            }
                          }}
                          className="ms-auto inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/5 px-3 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-40"
                          title={isAr ? "حذف" : "Delete"}
                        >
                          <Trash2 className="h-3 w-3" />
                          {isAr ? "حذف" : "Delete"}
                        </button>
                      )}
                    </div>

                    {isOpen && (
                      <div className="mt-3 space-y-2 border-t border-border pt-3">
                        {(r.internal_notes ?? []).map((n, i) => (
                          <div key={i} className="rounded-2xl bg-muted/50 p-2 text-xs">
                            <div className="text-[10px] text-muted-foreground">
                              {n.by_email ?? n.by.slice(0, 8)} · {new Date(n.at).toLocaleString()}
                            </div>
                            <div className="mt-1 whitespace-pre-wrap">{n.note}</div>
                          </div>
                        ))}
                        {perms.canModerate && (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={noteDraft[r.id] ?? ""}
                              onChange={(e) =>
                                setNoteDraft((d) => ({ ...d, [r.id]: e.target.value }))
                              }
                              placeholder={isAr ? "ملاحظة داخلية…" : "Internal note…"}
                              className="flex-1 rounded-full border border-border bg-background px-3 py-1.5 text-xs outline-none focus:border-primary"
                              maxLength={2000}
                            />
                            <button
                              disabled={noteMut.isPending || !noteDraft[r.id]?.trim()}
                              onClick={() =>
                                noteMut.mutate({ id: r.id, note: noteDraft[r.id].trim() })
                              }
                              className="rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-40"
                            >
                              {isAr ? "إضافة" : "Add"}
                            </button>
                          </div>
                        )}

                        {perms.canModerate && (
                          <div className="mt-2 rounded-2xl border border-primary/30 bg-primary/5 p-2">
                            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-primary">
                              {isAr ? "ردّ داخل التطبيق على الأب 💌" : "Reply in-app to the parent 💌"}
                            </div>
                            <textarea
                              value={replyDraft[r.id] ?? ""}
                              onChange={(e) =>
                                setReplyDraft((d) => ({ ...d, [r.id]: e.target.value }))
                              }
                              rows={3}
                              maxLength={4000}
                              placeholder={
                                isAr
                                  ? "ردّك سيظهر للأب في إعدادات حسابه + إيميل تنبيه قصير"
                                  : "Your reply appears in the parent's settings + a short email nudge"
                              }
                              className="w-full resize-none rounded-xl border border-border bg-background p-2 text-xs leading-relaxed outline-none focus:border-primary"
                            />
                            <div className="mt-1 flex justify-end">
                              <button
                                disabled={
                                  replyMut.isPending ||
                                  !(replyDraft[r.id] ?? "").trim() ||
                                  !r.user_id
                                }
                                onClick={() =>
                                  replyMut.mutate({
                                    feedbackId: r.id,
                                    parent_user_id: r.user_id,
                                    body: (replyDraft[r.id] ?? "").trim(),
                                  })
                                }
                                className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-40"
                              >
                                {replyMut.isPending
                                  ? isAr ? "جاري الإرسال…" : "Sending…"
                                  : isAr ? "إرسال للأب" : "Send to parent"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
